// Plays the soundtrack (songs.ts) live: a bar at a time, scheduled on the audio clock a little ahead of now, each
// layer on its own bus so the director's intensity brings instruments in and out on the bar line and the pad opens
// its filter as the race heats up. The kick pumps the rest of the band (a sidechain), a hall and a ping-pong delay
// give the band a room, a compressor glues it, and a piece fades into the next one instead of cutting.
import { midi, type Kit } from '../synth.js';
import type { Bus } from '../engine.js';

/** What the player needs of the audio bus: the context, the music bus and the shared noise (an offline render passes its own). */
export type MusicBus = Pick<Bus, 'music' | 'noise'> & { ac: BaseAudioContext };
import { INSTRUMENTS } from './instruments.js';
import { SONGS, LAYERS, barLen, beatLen, barNotes, type LayerName, type Song } from './songs.js';
import type { Cue } from './director.js';

/** Seconds of music scheduled ahead of the clock; more while the page is hidden, when timers come once a second. */
export const LOOKAHEAD_S = 1.2;
export const HIDDEN_LOOKAHEAD_S = 4;
/** Seconds a piece takes to fade into the next. */
export const FADE_S = 1.5;
/** Cutoff of the pad's filter at each intensity (Hz): it opens as the race heats up. */
export const PAD_CUTOFF = [900, 1500, 2800, 6000];
/** How far the band dips under each kick (gain) and how long it takes to come back (s). */
const PUMP = 0.42, PUMP_BACK_S = 0.09;

const SEND: Record<LayerName, { verb: number; delay: number }> = {
  pad: { verb: 0.35, delay: 0 }, arp: { verb: 0.25, delay: 0.2 }, drums: { verb: 0.08, delay: 0 }, perc: { verb: 0.15, delay: 0 },
  bass: { verb: 0, delay: 0 }, lead: { verb: 0.3, delay: 0.35 }, fx: { verb: 0.3, delay: 0 },
};
/** Layers the kick pumps: everything but the drums themselves and the effects. */
const PUMPED: readonly LayerName[] = ['pad', 'arp', 'bass', 'lead', 'perc'];

/** A hall: a dense stereo tail that darkens as it dies, longer than the sound bank's street. */
function hall(ac: BaseAudioContext, seconds: number): AudioBuffer {
  const sr = ac.sampleRate, n = Math.floor(sr * seconds), b = ac.createBuffer(2, n, sr);
  for (let c = 0; c < 2; c++) {
    const d = b.getChannelData(c);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const k = i / n, a = 0.45 - 0.38 * k;
      lp += a * ((Math.random() * 2 - 1) - lp);
      d[i] = lp * Math.pow(1 - k, 2.2) * Math.min(1, i / (sr * 0.02)) * 0.5;
    }
  }
  return b;
}

/** One piece playing: its graph, its clock and its layer buses. */
class SongVoice {
  readonly out: GainNode; readonly lp: BiquadFilterNode; readonly sc: GainNode;
  readonly layers = {} as Record<LayerName, GainNode>;
  readonly kits = {} as Record<LayerName, Kit>;
  readonly padFilter: BiquadFilterNode;
  private readonly verbSend: GainNode; private readonly delaySend: GainNode;
  bar = 0; nextBar: number; intensity: number; level = 1; fade = 1; gone = false;

  constructor(readonly b: MusicBus, readonly song: Song, cue: Cue) {
    const ac = b.ac, t = ac.currentTime;
    this.intensity = cue.intensity;
    // master of the piece: level and fade, muffle, glue
    this.out = ac.createGain(); this.out.gain.value = 0;
    this.lp = ac.createBiquadFilter(); this.lp.type = 'lowpass'; this.lp.frequency.value = 18000; this.lp.Q.value = 0.5;
    const comp = ac.createDynamicsCompressor();
    comp.threshold.value = -14; comp.knee.value = 6; comp.ratio.value = 3; comp.attack.value = 0.005; comp.release.value = 0.18;
    this.out.connect(this.lp).connect(comp).connect(b.music);
    // the sidechain: everything but the drums and the effects passes through it
    this.sc = ac.createGain(); this.sc.connect(this.out);
    // the room: a hall and a ping-pong delay on an eighth-note triplet-ish 3/16
    const conv = ac.createConvolver(), verbRet = ac.createGain(), verbCut = ac.createBiquadFilter();
    conv.buffer = hall(ac, 2.4); verbRet.gain.value = 0.3; verbCut.type = 'highpass'; verbCut.frequency.value = 250;
    this.verbSend = ac.createGain(); this.verbSend.connect(verbCut).connect(conv).connect(verbRet).connect(this.out);
    const dt = beatLen(song) * 0.75, dL = ac.createDelay(2), dR = ac.createDelay(2), fb = ac.createGain(), dCut = ac.createBiquadFilter(), pL = ac.createStereoPanner(), pR = ac.createStereoPanner(), dRet = ac.createGain();
    dL.delayTime.value = dt; dR.delayTime.value = dt; fb.gain.value = 0.38; dCut.type = 'lowpass'; dCut.frequency.value = 3500; pL.pan.value = -0.6; pR.pan.value = 0.6; dRet.gain.value = 0.5;
    this.delaySend = ac.createGain();
    this.delaySend.connect(dL); dL.connect(pL).connect(dRet); dL.connect(dCut).connect(dR); dR.connect(pR).connect(dRet); dR.connect(fb).connect(dL); dRet.connect(this.out);
    // the drums are driven a little: a dense, warm kick instead of a thin one (unity gain for small signals, the peaks rounded)
    const drive = ac.createWaveShaper(), curve = new Float32Array(512);
    for (let i = 0; i < 512; i++) { const x = (i / 511) * 2 - 1; curve[i] = Math.tanh(x * 1.6) / 1.6; }
    drive.curve = curve; drive.oversample = '2x'; drive.connect(this.out);
    // one bus per layer, with its sends; the pad behind a filter the intensity opens
    this.padFilter = ac.createBiquadFilter(); this.padFilter.type = 'lowpass'; this.padFilter.Q.value = 0.7; this.padFilter.frequency.value = PAD_CUTOFF[cue.intensity] ?? PAD_CUTOFF[0];
    for (const L of LAYERS) {
      const g = ac.createGain();
      g.gain.value = song.layers[L] && song.layers[L]!.from <= cue.intensity ? 1 : 0;
      g.connect(PUMPED.includes(L) ? this.sc : L === 'drums' ? drive : this.out);
      if (SEND[L].verb) { const s = ac.createGain(); s.gain.value = SEND[L].verb; g.connect(s).connect(this.verbSend); }
      if (SEND[L].delay) { const s = ac.createGain(); s.gain.value = SEND[L].delay; g.connect(s).connect(this.delaySend); }
      let head: AudioNode = g;
      if (L === 'pad') { this.padFilter.connect(g); head = this.padFilter; }
      this.layers[L] = g; this.kits[L] = { ac, out: head, noise: b.noise };
    }
    this.nextBar = t + 0.05;
    this.setLevel(cue.level, cue.muffle, 0.8);
  }

  /** Schedules every bar that starts before `horizon` (audio clock, s). */
  schedule(horizon: number) {
    const song = this.song, len = barLen(song), beat = beatLen(song);
    while (this.nextBar < horizon && !this.gone) {
      const t0 = this.nextBar;
      for (const nt of barNotes(song, this.bar, this.intensity)) {
        const at = t0 + nt.t * beat, dur = Math.max(0.03, nt.dur * beat);
        INSTRUMENTS[nt.inst](this.kits[nt.layer], at, dur, nt.n > 0 ? midi(nt.n) : 100, nt.vel);
        if (nt.inst === 'kick') this.pump(at);
      }
      this.bar++;
      this.nextBar += len;
    }
  }

  /** The band dips under a kick and comes straight back: the pulse of the mix. */
  private pump(at: number) {
    const g = this.sc.gain;
    g.setValueAtTime(1, at);
    g.linearRampToValueAtTime(PUMP, at + 0.012);
    g.setTargetAtTime(1, at + 0.04, PUMP_BACK_S);
  }

  /** Brings layers in and out and opens the pad, now or at `at` (audio clock, s). */
  setIntensity(i: number, at = this.b.ac.currentTime) {
    if (i === this.intensity) return;
    this.intensity = i;
    const t = at;
    for (const L of LAYERS) {
      const def = this.song.layers[L];
      this.layers[L].gain.setTargetAtTime(def && def.from <= i ? 1 : 0, t, 0.3);
    }
    this.padFilter.frequency.setTargetAtTime(PAD_CUTOFF[Math.max(0, Math.min(PAD_CUTOFF.length - 1, i))], t, 0.6);
  }

  /** Trim (0-1) and muffle, over `glide` seconds. */
  setLevel(level: number, muffle: boolean, glide = 0.25) {
    this.level = level;
    const t = this.b.ac.currentTime;
    this.out.gain.setTargetAtTime(this.level * this.fade, t, glide / 3);
    this.lp.frequency.setTargetAtTime(muffle ? 650 : 18000, t, 0.2);
  }

  /** Fades to silence and stops scheduling; the graph is released once the tail has died. */
  fadeOut(seconds: number, then: () => void) {
    this.fade = 0; this.gone = true;
    const t = this.b.ac.currentTime;
    this.out.gain.setTargetAtTime(0, t, seconds / 3);
    setTimeout(() => { this.out.disconnect(); then(); }, (seconds + 3) * 1000);
  }
}

/** Plays whatever the director cues: one piece at a time, crossfaded. */
export class MusicPlayer {
  private voice: SongVoice | null = null;
  private fading = new Set<SongVoice>();
  constructor(readonly b: MusicBus) {}

  /** The piece playing now, if any. */
  get playing(): string | null { return this.voice?.song.id ?? null; }

  /** Plays the cue; `at` (audio clock, s) defers an intensity change of the playing piece, for an offline render. */
  cue(c: Cue, at?: number) {
    const cur = this.voice;
    if (!c.song) { this.release(); return; }
    if (cur && cur.song.id === c.song) { cur.setIntensity(c.intensity, at); cur.setLevel(c.level, c.muffle); return; }
    this.release();
    this.voice = new SongVoice(this.b, SONGS[c.song], c);
    this.tick();
  }

  /** Lets the current piece go, fading. */
  release() {
    const v = this.voice;
    if (!v) return;
    this.voice = null;
    this.fading.add(v);
    v.fadeOut(FADE_S, () => this.fading.delete(v));
  }

  /** Schedules the next bars; call often (a timer, the ride's step). `hidden`: the page is not visible, timers are slow. */
  tick(hidden = false) {
    this.voice?.schedule(this.b.ac.currentTime + (hidden ? HIDDEN_LOOKAHEAD_S : LOOKAHEAD_S));
  }

  /** Schedules every bar starting before `t` (audio clock, s): an offline render asks for the whole piece at once. */
  scheduleUntil(t: number) { this.voice?.schedule(t); }

  /** Stops everything, fading. */
  stop() { this.release(); }
}
